import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import { CallMediaPermissionValue } from '@gem/call/CallStore/Call.types';
import {
  ICallPersonalizedPermissions,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallActionsPanelButton } from '@gem/call/components/CallActionsPanelButton/CallActionsPanelButton';
import { IcDoneIcon, IcMaxVolumeIcon, IcVoiceOff24Icon, IcVoiceOn24Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IconView, PopoverMenu, IPopoverMenuItem } from '@gem/ui-kit';

import { useCallAudioButtonPresenter } from './CallAudioButton.presenter';
import styles from './CallAudioButton.styles.m.css';

interface ICallAudioButtonProps {
  checkAndSendPermissionRequest(permissionType: PermissionsTypes): Promise<boolean>;
  onAudioPopoverOpen?(): void;
  permissions: ICallPersonalizedPermissions | null;
  className?: string;
  audioPermission: CallMediaPermissionValue;
  micControlEnabled: boolean;
}

export const CallAudioButton = observer(
  forwardRef<() => void, ICallAudioButtonProps>((props, ref) => {
    const { t } = useTranslation();

    const presenter = useCallAudioButtonPresenter({
      checkAndSendPermissionRequest: props.checkAndSendPermissionRequest,
      permissions: props.permissions,
      audioPermission: props.audioPermission,
      onAudioPopoverOpen: props.onAudioPopoverOpen,
      micControlEnabled: props.micControlEnabled,
    });
    const audioButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(
      ref,
      () => {
        return presenter.closeAudioPopover;
      },
      []
    );

    const audioMenuItems = useMemo(() => {
      const items: IPopoverMenuItem[] = [];
      if (presenter.micControlEnabled) {
        items.push({
          key: 'title',
          title: t('calls_equipment_select_microphone_header'),
          isDisabled: true,
          className: styles.menuTitle,
        });

        presenter.audioInputDevices.forEach((item) => {
          items.push({
            key: `${item.deviceId}-${item.kind}`,
            title: item.label,
            onClick: () => presenter.setCurrentDeviceId(item.kind, item.deviceId),
            iconSuffix:
              item.deviceId === presenter.currentAudioInputDeviceId ? (
                <div className={styles.checkIcon}>
                  <IconView Icon={IcDoneIcon} />
                </div>
              ) : undefined,
          });
        });
      }

      if (presenter.audioOutputDevices.length) {
        items.push({
          key: 'title-speaker',
          title: t('calls_equipment_select_speaker_header'),
          isDisabled: true,
          className: classNames(styles.menuTitle, styles.audioMenuTitle),
        });
      }

      presenter.audioOutputDevices.forEach((item) => {
        items.push({
          key: `${item.deviceId}-${item.kind}`,
          title: item.label,
          onClick: () => presenter.setCurrentDeviceId(item.kind, item.deviceId),
          iconSuffix:
            item.deviceId === presenter.currentAudioOutputDeviceId ? (
              <div className={styles.checkIcon}>
                <IconView Icon={IcDoneIcon} />
              </div>
            ) : undefined,
        });
      });

      return items;
    }, [
      presenter.audioInputDevices,
      presenter.currentAudioInputDeviceId,
      presenter.setCurrentDeviceId,
      presenter.audioOutputDevices,
      presenter.currentAudioOutputDeviceId,
      presenter.micControlEnabled,
    ]);

    return (
      <>
        <CallActionsPanelButton
          isArrowButtonPressed={presenter.isAudioPopoverOpened}
          setRef={audioButtonRef}
          Icon={
            presenter.micControlEnabled
              ? presenter.isAudioMuted
                ? IcVoiceOff24Icon
                : IcVoiceOn24Icon
              : IcMaxVolumeIcon
          }
          onClick={
            presenter.micControlEnabled
              ? presenter.isAudioMuted
                ? presenter.unmuteAudio
                : presenter.muteAudio
              : undefined
          }
          onArrowClick={
            presenter.isAudioPopoverOpened
              ? presenter.closeAudioPopover
              : presenter.onAudioArrowClick
          }
          className={props.className}
          theme={presenter.isAudioMuted ? '12' : '13'}
          isActive={!presenter.isAudioMuted}
          isDisabled={
            !presenter.isCallStarted || props.audioPermission === CallMediaPermissionValue.Denied
          }
          isLoading={!presenter.isCallStarted}
        />
        <PopoverMenu
          className={styles.popover}
          isOpened={presenter.isAudioPopoverOpened}
          placement="top-end"
          menuItems={audioMenuItems}
          reference={audioButtonRef.current}
          onClose={presenter.closeAudioPopover}
          theme={'dark'}
        />
      </>
    );
  })
);
